import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Tv, Film, Handshake, Mail, FileText, Activity, LogOut, Loader2, GraduationCap } from "lucide-react";
import { api, getAdminToken, clearAdminToken } from "../lib/api";
import { AdminLogin } from "./admin/AdminLogin";

const TABS = [
  { id: "channels", label: "Chaînes", icon: Tv, Comp: lazy(() => import("./admin/AdminChannels")) },
  { id: "videos", label: "Vidéos", icon: Film, Comp: lazy(() => import("./admin/AdminVideos")) },
  { id: "sponsors", label: "Partenaires", icon: Handshake, Comp: lazy(() => import("./admin/AdminSponsors")) },
  { id: "subs", label: "Abonnés", icon: Mail, Comp: lazy(() => import("./admin/AdminSubscribers")), count: "subs" },
  { id: "newsletter", label: "Newsletter", icon: Mail, Comp: lazy(() => import("./admin/AdminNewsletter")) },
  { id: "articles", label: "Articles", icon: FileText, Comp: lazy(() => import("./admin/AdminArticles")), count: "articles" },
  { id: "analytics", label: "Analytics", icon: Activity, Comp: lazy(() => import("./admin/AdminAnalytics")) },
  { id: "courses", label: "Cours", icon: GraduationCap, Comp: lazy(() => import("./admin/AdminCourses")) },
];

const Fallback = () => (
  <div className="glass rounded-xl p-12 text-center text-[color:var(--text-muted)]" data-testid="admin-tab-loading">
    <Loader2 className="w-6 h-6 mx-auto animate-spin" />
  </div>
);

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!getAdminToken());
  const [tab, setTab] = useState("channels");
  const [channels, setChannels] = useState([]);
  const [subs, setSubs] = useState([]);
  const [articles, setArticles] = useState([]);

  const reload = useCallback(() => {
    api.get("/channels").then(r => setChannels(r.data)).catch(() => {});
    api.get("/subscribers").then(r => setSubs(r.data)).catch(() => {});
    api.get("/articles").then(r => setArticles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/me").then(reload).catch(() => { clearAdminToken(); setAuthed(false); });
  }, [authed, reload]);

  useEffect(() => {
    const onLogout = () => setAuthed(false);
    window.addEventListener("fv-admin-logout", onLogout);
    return () => window.removeEventListener("fv-admin-logout", onLogout);
  }, []);

  const logout = () => { clearAdminToken(); setAuthed(false); };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const active = TABS.find(t => t.id === tab) || TABS[0];
  const shared = { channels, subs, articles, reload };
  const counts = { subs: subs.length, articles: articles.length };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="admin-page">
      <div className="flex items-center gap-4 mb-8">
        <h1 className="font-display text-5xl">Régie de diffusion</h1>
        <span className="ml-auto live-dot" />
        <span className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Admin</span>
        <button onClick={logout} data-testid="admin-logout" title="Se déconnecter" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--accent)] border border-[color:var(--border)] hover:border-[color:var(--accent)] px-3 py-1.5 rounded-full transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Quitter
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-full transition-colors ${tab === t.id ? "bg-[color:var(--accent)] border-[color:var(--accent)] text-white" : "border-[color:var(--border)] hover:border-[color:var(--accent)]"}`}>
            <t.icon className="w-4 h-4 inline mr-2" /> {t.label}{t.count ? ` (${counts[t.count]})` : ""}
          </button>
        ))}
      </div>

      <Suspense fallback={<Fallback />}>
        <active.Comp {...shared} />
      </Suspense>
    </div>
  );
}
